import { useCallback } from "react";
import { useApolloClient, useMutation } from "@apollo/client/react";

import {
  CreateCustomRoleDocument,
  PermissionMatrixDocument,
  RoleInviteGrantMatrixDocument,
  SetRoleInviteGrantDocument,
  SetRolePermissionDocument,
} from "#app/lib/data";
import type { CellState, SystemRole } from "@salestrak/permissions";

// Data layer for the Permissions page's "New role" dialog. The
// createCustomRole mutation seeds the new role with the template
// system role's cell defaults; this hook chains follow-up
// setRolePermission / setRoleInviteGrant writes for any cell the
// admin toggled away from that default, THEN refetches both matrices.
// The refetch must come after every write — hanging refetchQueries
// off createCustomRole snapshots the matrices before the override
// writes land, which made fresh roles render with stale (all-off)
// invite toggles until a manual refresh.

const TEMPLATE_SYSTEM_ROLES: Record<SystemRole, string> = {
  guest: "guest",
  limited_member: "limited_member",
  member: "member",
  admin: "admin",
  owner: "owner",
};

export function useCreateCustomRole(workspaceId: string) {
  const client = useApolloClient();
  const [createCustomRole] = useMutation(CreateCustomRoleDocument);
  const [setRolePermission] = useMutation(SetRolePermissionDocument);
  const [setRoleInviteGrant] = useMutation(SetRoleInviteGrantDocument);

  return useCallback(
    async (args: {
      roleName: string;
      template: SystemRole;
      // Cells that should override the template's seeded defaults.
      // Keyed by permission key. Cells matching the template default
      // can be omitted — they're already correct after createCustomRole.
      overrides: Record<string, CellState>;
      // Invite grants to enable for the new role as inviter. Custom
      // roles seed as `no` for every editable pair, so only the
      // toggled-ON targets need a write.
      inviteGrants?: Array<{ invitableRoleId: string; granted: boolean }>;
    }) => {
      const result = await createCustomRole({
        variables: {
          input: {
            workspaceId,
            roleName: args.roleName.trim(),
            // SystemRoleGQL enum mirrors our SystemRole keys verbatim.
            templateSystemRole: TEMPLATE_SYSTEM_ROLES[args.template] as never,
          },
        },
      });
      const roleId = result.data?.createCustomRole as string | undefined;
      if (!roleId) return null;
      try {
        // Fire overrides sequentially — small N, keep ordering stable so
        // any per-cell server validation shows in order.
        for (const [key, state] of Object.entries(args.overrides)) {
          await setRolePermission({
            variables: {
              input: { roleId, permissionKey: key, state: state as never },
            },
          });
        }
        for (const grant of args.inviteGrants ?? []) {
          await setRoleInviteGrant({
            variables: {
              input: {
                inviterRoleId: roleId,
                invitableRoleId: grant.invitableRoleId,
                granted: grant.granted,
              },
            },
          });
        }
      } finally {
        // Refetch AFTER all writes so the matrices reflect the final
        // state — in the finally so a failed override still refreshes
        // to whatever actually persisted.
        await client.refetchQueries({
          include: [PermissionMatrixDocument, RoleInviteGrantMatrixDocument],
        });
      }
      return roleId;
    },
    [
      workspaceId,
      client,
      createCustomRole,
      setRolePermission,
      setRoleInviteGrant,
    ],
  );
}
